import { useState } from "react";
import { Copy, Trash2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";

export function BookingQuickActions({
  booking,
  canCancel,
  canRemove,
  canRepeat,
  cancelFromPrep = false,
  onCancelled,
  onRemoved,
  onRepeat,
}) {
  const [open, setOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const cancel = async () => {
    setCancelling(true);
    try {
      if (cancelFromPrep) {
        await api.post(`/bookings/${booking.id}/status`, { status: "Cancelled" });
      } else {
        await api.post(`/bookings/${booking.id}/cancel`, {
          reason: "Cancelled from My bookings",
        });
      }
      toast.success(`${booking.reference} cancelled`);
      setOpen(false);
      onCancelled?.();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setCancelling(false);
    }
  };
  const remove = async () => {
    setRemoving(true);
    try {
      await api.post(`/bookings/${booking.id}/hide`);
      toast.success(`${booking.reference} removed from My bookings`);
      setRemoveOpen(false);
      onRemoved?.();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setRemoving(false);
    }
  };
  if (!canCancel && !canRepeat && !canRemove) return null;
  return (
    <div className="flex shrink-0 items-center gap-1" data-testid={`booking-quick-actions-${booking.reference}`}>
      {canRepeat && (
        <Button
          size="sm"
          variant="ghost"
          title="Repeat booking"
          onClick={onRepeat}
          data-testid={`booking-quick-repeat-${booking.reference}`}
        >
          <Copy className="mr-1.5 h-4 w-4" />
          Repeat
        </Button>
      )}
      {canCancel && (
        <AlertDialog open={open} onOpenChange={setOpen}>
          <AlertDialogTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              data-testid={`booking-quick-cancel-${booking.reference}`}
            >
              <XCircle className="mr-1.5 h-4 w-4" />
              Cancel
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid={`booking-cancel-confirmation-${booking.reference}`}>
            <AlertDialogHeader>
              <AlertDialogTitle>{`Cancel ${booking.reference}?`}</AlertDialogTitle>
              <AlertDialogDescription>
                This will cancel the booking and notify the relevant department staff.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid={`booking-cancel-keep-${booking.reference}`}>
                Keep booking
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={cancelling}
                onClick={cancel}
                data-testid={`booking-cancel-confirm-${booking.reference}`}
              >
                {cancelling ? "Cancelling…" : "Cancel booking"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {canRemove && (
        <AlertDialog open={removeOpen} onOpenChange={setRemoveOpen}>
          <AlertDialogTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              title="Remove from My bookings"
              data-testid={`booking-quick-remove-${booking.reference}`}
            >
              <Trash2 className="mr-1.5 h-4 w-4" />
              Remove
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid={`booking-remove-confirmation-${booking.reference}`}>
            <AlertDialogHeader>
              <AlertDialogTitle>{`Remove ${booking.reference} from My bookings?`}</AlertDialogTitle>
              <AlertDialogDescription>
                This only hides the cancelled booking from your personal list. The department
                record and audit history are retained.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid={`booking-remove-keep-${booking.reference}`}>
                Keep booking
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={removing}
                onClick={remove}
                data-testid={`booking-remove-confirm-${booking.reference}`}
              >
                {removing ? "Removing…" : "Remove booking"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}